import { Check, ChevronRight } from 'lucide-react';
import type { z } from 'zod';
import type { choiceCard } from '../../shared/registry/choice-card.ts';
import { cn } from '../lib/cn.ts';

type Props = z.output<typeof choiceCard.params> & {
  picked?: string;
  onPick?: (option: { id: string; label: string }) => void;
};

export function ChoiceCard({ question, options, picked, onPick }: Props) {
  return (
    <article className="card ring-1 ring-fg/10">
      <h3 className="text-base font-semibold text-fg">{question}</h3>
      <div className="mt-4 grid gap-2">
        {options.map((o) => {
          const chosen = picked === o.id;
          return (
            <button
              key={o.id}
              type="button"
              data-testid={`choice-${o.id}`}
              disabled={Boolean(picked)}
              onClick={() => onPick?.({ id: o.id, label: o.label })}
              className={cn(
                'group flex items-center gap-3 rounded-2xl px-4 py-3 text-left ring-1 transition',
                chosen
                  ? 'bg-accent/15 ring-accent/50'
                  : 'bg-fg/[0.03] ring-fg/10 hover:bg-fg/[0.07] disabled:opacity-40 disabled:hover:bg-fg/[0.03]',
              )}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-medium text-fg">{o.label}</span>
                {o.hint ? <span className="block text-sm text-subtle">{o.hint}</span> : null}
              </span>
              {chosen ? (
                <Check className="size-5 text-accent" aria-hidden />
              ) : (
                <ChevronRight className="size-5 text-faint transition group-hover:translate-x-0.5" aria-hidden />
              )}
            </button>
          );
        })}
      </div>
    </article>
  );
}
